import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, PlusCircle, Rocket, XCircle, Loader2 } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";
import { formatApiError } from "@/lib/api";

export default function Reservations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/reservations", { params })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load, statusFilter]);

  const doAction = async (id, kind) => {
    try {
      await api.post(`/reservations/${id}/${kind}`);
      toast.success(kind === "deploy" ? "Reservation deployed" : "Reservation cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Lifecycle · Advanced</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Device Reservations</h1>
          <p className="text-sm text-slate-500 mt-0.5">Hold devices for future deployments. Double-booking is blocked.</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="reservations-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="deployed">Deployed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="reservations-new-btn">
                <PlusCircle className="h-4 w-4 mr-1.5" /> New reservation
              </Button>
            </DialogTrigger>
            <NewReservationDialog onDone={() => { setShowCreate(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Code</th>
              <th className="text-left px-4 py-2.5">Device</th>
              <th className="text-left px-4 py-2.5">Employee</th>
              <th className="text-left px-4 py-2.5">Window</th>
              <th className="text-left px-4 py-2.5">Purpose</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                <CalendarClock className="h-6 w-6 mx-auto mb-2 text-slate-300" strokeWidth={1.5} />
                No reservations yet
              </td></tr>
            ) : items.map((r) => (
              <tr key={r.id} data-testid={`reservation-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{r.reservation_code}</td>
                <td className="px-4 py-2.5">
                  {r.asset ? (
                    <div>
                      <div className="font-medium text-slate-800">{r.asset.brand} {r.asset.model}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.asset.serial}</div>
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-2.5">
                  {r.employee ? (
                    <div>
                      <div className="text-slate-800">{r.employee.name}</div>
                      <div className="text-xs text-slate-500">{r.employee.email}</div>
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-600">
                  <div>{new Date(r.start_date).toLocaleDateString()}</div>
                  <div className="text-slate-400">→ {new Date(r.end_date).toLocaleDateString()}</div>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{r.purpose || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge value={r.status} /></td>
                <td className="px-4 py-2.5 text-right">
                  {r.status === "active" && (
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => doAction(r.id, "cancel")} data-testid={`reservation-cancel-${r.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" className="bg-slate-900" onClick={() => doAction(r.id, "deploy")} data-testid={`reservation-deploy-${r.id}`}>
                        <Rocket className="h-3.5 w-3.5 mr-1" /> Deploy
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewReservationDialog({ onDone }) {
  const [assets, setAssets] = useState([]);
  const [emps, setEmps] = useState([]);
  const [assetQuery, setAssetQuery] = useState("");
  const [empQuery, setEmpQuery] = useState("");
  const [form, setForm] = useState({
    asset_id: "", employee_id: "", start_date: "", end_date: "", purpose: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/assets/", { params: { available_only: true, q: assetQuery || undefined, limit: 15 } })
      .then(({ data }) => setAssets(data.items || []));
  }, [assetQuery]);
  useEffect(() => {
    api.get("/employees", { params: { q: empQuery || undefined, limit: 15 } })
      .then(({ data }) => setEmps(data.items || []));
  }, [empQuery]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/reservations", form);
      toast.success("Reservation created");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };
  const valid = form.asset_id && form.employee_id && form.start_date && form.end_date;

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>New device reservation</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-xs">Employee</Label>
          <Input placeholder="Search employee…" value={empQuery} onChange={(e) => setEmpQuery(e.target.value)} className="mt-1 h-9 mb-1.5" />
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger className="h-10" data-testid="reservation-employee-select"><SelectValue placeholder="Select employee…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Device (available inventory)</Label>
          <Input placeholder="Search serial, tag or model…" value={assetQuery} onChange={(e) => setAssetQuery(e.target.value)} className="mt-1 h-9 mb-1.5" />
          <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
            <SelectTrigger className="h-10" data-testid="reservation-asset-select"><SelectValue placeholder="Select asset…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.brand} {a.model} · {a.serial}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Start date</Label>
            <Input type="date" className="mt-1 h-10" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="reservation-start-date" />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input type="date" className="mt-1 h-10" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="reservation-end-date" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Purpose</Label>
          <Input className="mt-1 h-10" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Onboarding — starts 3 Mar" />
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button className="bg-slate-900" disabled={!valid || submitting} onClick={submit} data-testid="reservation-submit-btn">
          {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-1.5" />}
          Reserve device
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
